import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select, Row } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  clearCustomers,
  clearStyle,
  clearUnitIds,
  getCustomers,
  getStyle,
  getUnitIds,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const UpdateStyle = props => {
  const { classes, match } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, reset } = useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const unitIds = useSelector(state => state.unitIds);
  const style = useSelector(state => state.style);
  const customers = useSelector(state => state.customers);

  useEffect(() => {
    dispatch(getUnitIds());
    dispatch(getCustomers());
    dispatch(getStyle(match.params.id));
    return () => {
      dispatch(clearUnitIds());
      dispatch(clearCustomers());
      dispatch(clearStyle());
    };
  }, []);

  useEffect(() => {
    reset(style);
  }, [style]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.put(`/styles/${match.params.id}`, formValues);
      successNotification('Style updated');
      history.push('/styles/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Style"
        moduleLink="/styles/list"
        subModuleName="Update"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  label="Name"
                  placeholder="Name"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Description"
                  placeholder="Description"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="length"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Length"
                  placeholder="Length"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="width"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Width"
                  placeholder="Width"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="height"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Height"
                  placeholder="Height"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="flap"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Flap"
                  placeholder="Flap"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="gsm"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="GSM"
                  placeholder="GSM"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="unitId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Unit"
                  placeholder="Select unit"
                  {...field}
                >
                  {unitIds.map(unit => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.unit}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="diameter"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Diameter"
                  placeholder="Diameter"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

UpdateStyle.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(UpdateStyle);
